import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Download, Eye, FileText, History, Lock, Trash2 } from 'lucide-react';
import {
  backofficeApi,
  extractErrorMessage,
  formatBytes,
  formatDate,
  formatDateTime,
} from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { BackofficeClaimEvidenceDto, ClaimEvidenceAccessDto } from '@kardume/types';
import { ActionButton, Badge, Modal, Table, TableContainer } from '@kardume/ui';
import DataState from './DataState';

/**
 * Os anexos de um pedido de reivindicação (card 277, regra §37.12).
 *
 * ## Listar não é abrir
 *
 * Este painel carrega **só metadado** - nome, tipo, tamanho, prazo de guarda e
 * quantas vezes o arquivo já foi aberto. Nada disso registra acesso, e é por
 * isso que a lista pode aparecer sozinha ao abrir o detalhe.
 *
 * O conteúdo **nunca** é pré-carregado. Abrir é um clique explícito, exige
 * outra permissão (`CLAIM_EVIDENCE_READ`) e grava quem, quando e de onde.
 * Buscar o arquivo "para agilizar" gravaria, com o nome de quem só passou os
 * olhos na fila, um acesso que ninguém pediu - e a trilha existe justamente
 * para responder quem leu o contrato social de um terceiro.
 *
 * ## A contagem fica à vista
 *
 * `accessCount` é coluna, não detalhe escondido: "este contrato foi aberto onze
 * vezes" é a informação que faz alguém perguntar por quê, e ela não seria
 * notada num relatório que ninguém abre.
 *
 * ## Expurgado continua na lista
 *
 * Documento cujo prazo de guarda venceu responde 404 no conteúdo, mas a linha
 * permanece - apagá-la levaria junto o registro de que ele existiu e de quem o
 * consultou. A tela mostra a data do expurgo no lugar do botão, em vez de um
 * botão que só falha quando clicado.
 */

const Panel = styled.section`
  display: grid;
  gap: 0.6rem;
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(p) => p.theme.textMuted};
`;

const Note = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const Restricted = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 0.85rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.83rem;
  color: ${(p) => p.theme.textSecondary};
`;

const FileCell = styled.div`
  min-width: 0;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
  small {
    display: block;
    font-size: 0.76rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

/* A contagem de aberturas ganha peso quando cresce. Não é um limite - é o
   número que deve puxar o olho de quem passa pela lista. */
const Opens = styled.span<{ $many: boolean }>`
  font-weight: ${(p) => (p.$many ? 700 : 500)};
  color: ${(p) => (p.$many ? p.theme.warning : p.theme.textSecondary)};
`;

const Purged = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const Actions = styled.div`
  display: inline-flex;
  gap: 0.35rem;
  justify-content: flex-end;
`;

const TrailList = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.5rem 1rem;
  margin: 0;
  font-size: 0.86rem;

  dt {
    color: ${(p) => p.theme.textPrimary};
  }
  dd {
    margin: 0;
    color: ${(p) => p.theme.textMuted};
    white-space: nowrap;
  }
`;

/** Acima disto a contagem de aberturas é destacada. Convite a perguntar, não limite. */
const MANY_OPENS = 5;

interface Props {
  claimId: string;
  /** Permissões efetivas de quem está na tela. */
  permissions: string[];
  isAdmin: boolean;
}

const ClaimEvidencePanel: React.FC<Props> = ({ claimId, permissions, isAdmin }) => {
  const { showNotification } = useNotification();

  const canList = isAdmin || ['ROLE_BACKOFFICE_CLAIM_READ', 'ROLE_BACKOFFICE_CLAIM_MANAGE'].some((code) => permissions.includes(code));
  const canOpen = isAdmin || permissions.includes('ROLE_BACKOFFICE_CLAIM_EVIDENCE_READ');
  const canAudit = isAdmin || permissions.includes('ROLE_BACKOFFICE_AUDIT_READ');

  const [evidences, setEvidences] = useState<BackofficeClaimEvidenceDto[]>([]);
  const [loading, setLoading] = useState(canList);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [trail, setTrail] = useState<{ evidence: BackofficeClaimEvidenceDto; rows: ClaimEvidenceAccessDto[] } | null>(
    null,
  );

  const load = useCallback(() => {
    if (!canList) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    backofficeApi
      .listClaimEvidences(claimId)
      .then(setEvidences)
      .catch((err) => setError(extractErrorMessage(err, 'Não foi possível carregar os anexos.')))
      .finally(() => setLoading(false));
  }, [claimId, canList]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Abre o documento - e é aqui, e só aqui, que o acesso é registrado.
   *
   * O servidor devolve com `Content-Disposition: attachment`, o que já diz que
   * o arquivo não deve ser exibido dentro do painel: um PDF aberto numa aba
   * fica no cache do navegador de quem analisou, e o objetivo é que o
   * documento circule o mínimo possível.
   */
  const openEvidence = async (evidence: BackofficeClaimEvidenceDto): Promise<void> => {
    setOpening(evidence.id);
    try {
      const { blob, fileName } = await backofficeApi.openClaimEvidence(evidence.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || evidence.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showNotification('Documento aberto. A abertura ficou registrada na trilha.', 'success');
      // A contagem mudou no servidor por causa deste clique; recarregar mantém
      // a coluna honesta sem que alguém precise fechar e reabrir o detalhe.
      load();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível abrir o documento. Se o prazo de guarda venceu, o arquivo já foi apagado.',
        ),
        'error',
      );
    } finally {
      setOpening(null);
    }
  };

  const openTrail = async (evidence: BackofficeClaimEvidenceDto): Promise<void> => {
    try {
      const rows = await backofficeApi.listClaimEvidenceAccesses(evidence.id);
      setTrail({ evidence, rows });
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível carregar a trilha de acesso.'), 'error');
    }
  };

  if (!canList) {
    return (
      <Restricted>
        <Lock size={15} /> Ver os anexos deste pedido exige a permissão de reivindicações.
      </Restricted>
    );
  }

  return (
    <Panel>
      <PanelTitle>
        <FileText size={14} /> Anexos do pedido
      </PanelTitle>
      <Note>
        {canOpen
          ? 'Abrir um documento registra quem abriu, quando e de onde. A lista abaixo não registra nada.'
          : 'Você vê que os documentos existem, mas abrir exige a permissão de leitura de evidências.'}
      </Note>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        isEmpty={evidences.length === 0}
        emptyTitle="Sem anexos"
        emptyDescription="Este pedido não teve documento enviado."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Arquivo</th>
                <th>Tamanho</th>
                <th>Enviado em</th>
                <th>Aberturas</th>
                <th>Guarda</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {evidences.map((evidence) => (
                <tr key={evidence.id}>
                  <td>
                    <FileCell>
                      <strong>{evidence.fileName}</strong>
                      <small>{evidence.contentType}</small>
                    </FileCell>
                  </td>
                  <td>{formatBytes(evidence.sizeBytes)}</td>
                  <td>{formatDate(evidence.createdAt)}</td>
                  <td>
                    <Opens $many={evidence.accessCount >= MANY_OPENS}>{evidence.accessCount}</Opens>
                  </td>
                  <td>
                    {evidence.purgedAt ? (
                      <Badge tone="secondary">Expurgado</Badge>
                    ) : evidence.purgeAfter ? (
                      <Purged>até {formatDate(evidence.purgeAfter)}</Purged>
                    ) : (
                      <Purged>pedido aberto</Purged>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Actions>
                      {/* Documento expurgado não ganha botão quebrado: a data
                          do expurgo é a resposta, e ela já é a informação. */}
                      {!evidence.available || evidence.purgedAt ? (
                        <Purged>
                          <Trash2 size={13} /> apagado em {formatDate(evidence.purgedAt)}
                        </Purged>
                      ) : (
                        canOpen && (
                          <ActionButton
                            title="Abrir o documento (fica registrado)"
                            disabled={opening === evidence.id}
                            onClick={() => openEvidence(evidence)}
                          >
                            <Download size={16} />
                          </ActionButton>
                        )
                      )}
                      {canAudit && (
                        <ActionButton title="Quem abriu este documento" onClick={() => openTrail(evidence)}>
                          <History size={16} />
                        </ActionButton>
                      )}
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {/* A trilha sobrevive ao arquivo: depois do expurgo ela continua
          respondendo quem consultou o documento enquanto ele existia. */}
      <Modal
        isOpen={!!trail}
        onClose={() => setTrail(null)}
        title={trail ? `Quem abriu "${trail.evidence.fileName}"` : ''}
        width="520px"
      >
        {trail && (
          <Panel>
            {trail.rows.length === 0 ? (
              <Note>
                <Eye size={13} /> Nunca foi aberto.
              </Note>
            ) : (
              <TrailList>
                {trail.rows.map((row, index) => (
                  <React.Fragment key={`${row.occurredAt}-${index}`}>
                    <dt>
                      {row.actorName || 'autor desconhecido'}
                      {row.actorIp ? ` · ${row.actorIp}` : ''}
                    </dt>
                    <dd>{formatDateTime(row.occurredAt)}</dd>
                  </React.Fragment>
                ))}
              </TrailList>
            )}
          </Panel>
        )}
      </Modal>
    </Panel>
  );
};

export default ClaimEvidencePanel;
